/**
 * Two-column shell for the sign-in and sign-up pages: the form on the left,
 * a tilted preview of the dashboard on the right (large screens only).
 */
import { type ReactNode } from "react";
import { useState } from "react";
import { Logo } from "@/components/ui/logo";
import { DashboardPreview } from "./DashboardPreview";

const API_URL = (import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? "/api";

export function AuthLayout({
  title,
  subtitle,
  children,
  footer,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  /** Small print under the form, e.g. terms */
  footer?: ReactNode;
}) {
  return (
    <div className="grid h-screen grid-cols-1 bg-background lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
      {/* Form */}
      <div className="flex h-screen flex-col overflow-y-auto px-6 py-6 sm:px-10">
        <Logo variant="full" height={24} />

        <div className="flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-sm space-y-6">
            <div className="space-y-1.5">
              <h1 className="text-2xl leading-tight text-balance">{title}</h1>
              <p className="font-sans text-sm text-muted-foreground">{subtitle}</p>
            </div>
            {children}
          </div>
        </div>

        {footer && <div className="font-sans text-[11px] text-muted-foreground">{footer}</div>}
      </div>

      {/* Product preview */}
      <div className="relative hidden overflow-hidden border-l border-border bg-muted/40 lg:block">
        {/* Brand grid backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "linear-gradient(hsl(var(--border)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--border)) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
            maskImage: "radial-gradient(ellipse at 30% 40%, black 30%, transparent 80%)",
            WebkitMaskImage: "radial-gradient(ellipse at 30% 40%, black 30%, transparent 80%)",
          }}
        />

        <div className="relative z-10 max-w-md px-12 pt-14 space-y-2">
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Built for AI companies</p>
          <p className="text-3xl font-semibold leading-tight tracking-tight text-balance">
            Know when a competitor reprices, relaunches or pivots.
          </p>
        </div>

        <DashboardPreview className="absolute left-[14%] top-[34%]" />

        {/* Fade the preview into the panel at the bottom and right edges */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, transparent 70%, hsl(var(--muted)) 100%), linear-gradient(to right, transparent 80%, hsl(var(--muted) / 0.6) 100%)",
          }}
        />
      </div>
    </div>
  );
}


export function OrDivider() {
  return (
    <div className="relative">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full border-t border-border" />
      </div>
      <div className="relative flex justify-center">
        <span className="bg-background px-3 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">or</span>
      </div>
    </div>
  );
}

export function GoogleButton() {
  const [isLoading, setIsLoading] = useState(false);

  const handleGoogleClick = () => {
    setIsLoading(true);
    // Direct redirect flow: no popup, no localStorage communication
    // User redirects to Google, Google redirects back to /auth/callback, 
    // OAuthCallbackPage handles token and navigates
    window.location.href = `${API_URL}/oauth/google?origin=${encodeURIComponent(window.location.origin)}`;
  };

  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={handleGoogleClick}
        disabled={isLoading}
        className="flex w-full items-center justify-center gap-2.5 rounded-lg border border-border bg-background px-4 py-2.5 font-mono text-[11px] uppercase tracking-widest text-foreground hover:bg-accent transition-colors disabled:cursor-wait disabled:opacity-60"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
        {isLoading ? "Redirecting to Google..." : "Continue with Google"}
      </button>
    </div>
  );
}
